import { ApiError, REQUEST_TIMED_OUT, demoApi, problemMessage } from "@/api/client";
import type { TimeOffRequest } from "@/api/types";
import { withDrainLock, DRAIN_LOCKS } from "./drainLock";
import { createTimeOffOutboxStore, type TimeOffOutboxStore, type QueuedTimeOff } from "./outboxStore";
import { registerDrainTriggers } from "./drainTriggers";

/**
 * A queued time-off submission the server refused OUTRIGHT once it finally replayed — a
 * 4xx, not a network problem. By the time anything can render this the item is already
 * gone from the queue (see `drainOnce` below), so this is delivered as its own event
 * rather than left for a UI to infer from a row that vanished (ui-breadcrumbs #1: state
 * belongs in the DOM, never inferred). Mirrors `punchOutbox.ts`'s own `PunchRefusal`.
 */
export interface TimeOffRefusal {
  clientRequestId: string;
  reason: string;
}

type Listener = (items: QueuedTimeOff[]) => void;
/** Fires once a queued item has actually landed server-side, carrying the REAL row the
 *  server returned — never a locally-guessed shape. `TimeOffRequests.tsx` uses this to
 *  replace the queued placeholder with the real, persisted request the moment it exists,
 *  rather than racing its own `GET /time-off/mine` refresh against the drain's own POST. */
type LandedListener = (clientRequestId: string, landed: TimeOffRequest) => void;
type RefusalListener = (refusal: TimeOffRefusal) => void;

function newClientRequestId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  // Same fallback as offline/outbox.ts's newClientId / punchOutbox.ts's newClientPunchId —
  // older iOS Safari (< 15.4) has no crypto.randomUUID. This id never leaves the device
  // except as a replay's dedupe key, so the (tiny) collision odds are an acceptable trade
  // against a hard runtime error.
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * ef-040-5 — on-device outbox for a time-off submission the phone could not deliver, the
 * time-off counterpart to `offline/punchOutbox.ts` and deliberately built on the SAME
 * three pieces that queue already proved out: `outboxStore.ts` (verify-by-real-write store
 * selection), `drainLock.ts` (one tab drains at a time, across tabs), and
 * `drainTriggers.ts` (backoff poll + online + load). A plain singleton, not React state,
 * for the same reason as `punchOutbox`: the queue has to keep draining whether or not
 * `TimeOffRequests` happens to be mounted.
 *
 * IDEMPOTENT SERVER-SIDE, SAME AS A PUNCH REPLAY (ef-016's `clientPunchId` — here,
 * `clientRequestId`, db/migrations/0039_time_off_offline.sql) — see `punchOutbox.ts`'s own
 * remarks on why that changes the right answer for both a genuine refusal (drop and say
 * why — retrying a 4xx would never succeed) and an ambiguous failure (hold and retry,
 * because a duplicate replay is a no-op server-side).
 */
class TimeOffOutboxManager {
  private storePromise: Promise<TimeOffOutboxStore> | null = null;
  private listeners = new Set<Listener>();
  private landedListeners = new Set<LandedListener>();
  private refusalListeners = new Set<RefusalListener>();
  private draining = false;

  private getStore(): Promise<TimeOffOutboxStore> {
    if (!this.storePromise) this.storePromise = createTimeOffOutboxStore();
    return this.storePromise;
  }

  async list(): Promise<QueuedTimeOff[]> {
    const store = await this.getStore();
    return store.all();
  }

  /** Live view of the queue, oldest first — what `TimeOffRequests.tsx` renders alongside
   *  the real, server-confirmed requests. */
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /** One-shot notifications for a submission that just landed — see `LandedListener`
   *  above. */
  subscribeLanded(fn: LandedListener): () => void {
    this.landedListeners.add(fn);
    return () => {
      this.landedListeners.delete(fn);
    };
  }

  /** One-shot notifications for a dropped, refused replay — see `TimeOffRefusal` above. */
  subscribeRefusals(fn: RefusalListener): () => void {
    this.refusalListeners.add(fn);
    return () => {
      this.refusalListeners.delete(fn);
    };
  }

  private async notify(): Promise<void> {
    const items = await this.list();
    for (const fn of this.listeners) fn(items);
  }

  private notifyLanded(clientRequestId: string, landed: TimeOffRequest): void {
    for (const fn of this.landedListeners) fn(clientRequestId, landed);
  }

  private notifyRefusal(refusal: TimeOffRefusal): void {
    for (const fn of this.refusalListeners) fn(refusal);
  }

  /**
   * Queue a submission the live attempt could not deliver
   * (`ApiError.neverReachedTheServer`, or a plain `!navigator.onLine` check made before
   * ever attempting the live call — see `TimeOffRequests.tsx`'s submit handler).
   * `recordedAt` MUST be the phone's clock captured by the caller AT THE TAP, before this
   * call — never re-derived here, or a slow enqueue would silently shift the fact the
   * server is later asked to trust as when the worker actually asked.
   */
  async enqueue(item: Omit<QueuedTimeOff, "clientRequestId" | "queuedAt">): Promise<string> {
    const store = await this.getStore();
    const clientRequestId = newClientRequestId();
    await store.add({ ...item, clientRequestId, queuedAt: Date.now() });
    await this.notify();
    return clientRequestId;
  }

  /**
   * Replay the outbox in order (oldest first) — sequential and awaited one at a time,
   * exactly like `punchOutbox.ts`'s own `drain`. The guard flag and the cross-tab Web Lock
   * below prevent two overlapping drains from replaying the same queued submission twice.
   */
  async drain(): Promise<void> {
    if (this.draining) return;
    this.draining = true;
    try {
      await withDrainLock(DRAIN_LOCKS.timeOff, async () => {
        try {
          await this.drainOnce();
        } finally {
          // A tab queued behind the lock may find the store already emptied by the other tab and
          // process nothing (or bail still-offline); without this its subscribers keep a stale view.
          await this.notify();
        }
      });
    } finally {
      this.draining = false;
    }
  }

  /** One pass over the queue. Only ever called while the time-off drain lock is held. */
  private async drainOnce(): Promise<void> {
    const store = await this.getStore();
    const items = await store.all();

    for (const item of items) {
      let landed: TimeOffRequest;
      try {
        landed = await demoApi.submitTimeOff({
          startDate: item.startDate,
          endDate: item.endDate,
          hoursRequested: item.hoursRequested,
          reason: item.reason,
          clientRequestId: item.clientRequestId,
          recordedAt: item.recordedAt,
        });
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.neverReachedTheServer || err.status === REQUEST_TIMED_OUT || err.status >= 500)
        ) {
          // Held like an outage — see punchOutbox.ts's own remarks on this exact branch.
          // Stop here, IN ORDER, so nothing later in the queue is attempted ahead of this
          // one; the next drain trigger resumes from this same item.
          return;
        }
        if (err instanceof ApiError) {
          // A genuine, permanent refusal (4xx) — idempotency does not help here, the
          // server has already made its decision and a retry would return the same
          // answer forever.
          await store.remove(item.clientRequestId);
          this.notifyRefusal({
            clientRequestId: item.clientRequestId,
            reason: problemMessage(err, "The server refused this time-off request."),
          });
          await this.notify();
          continue;
        }
        // Not an ApiError at all — an unexpected thrown value. Leave it queued rather than
        // guessing at what happened; the next drain attempt gets another chance.
        console.warn("A time-off drain attempt failed unexpectedly; retrying with backoff.", err);
        return;
      }

      await store.remove(item.clientRequestId);
      this.notifyLanded(item.clientRequestId, landed);
      await this.notify();
    }
  }
}

export const timeOffOutbox = new TimeOffOutboxManager();

registerDrainTriggers(timeOffOutbox);
